import { EuiButtonEmpty, EuiContextMenu, EuiIcon, EuiPopover, EuiSpacer } from "@elastic/eui";
import React, { useContext, useState } from "react";

import { useScreensQuery } from "../api/react-query/queries/screens";
import { toOptionalArrayEntry } from "../utils/optionalArray";
import { ShopContext } from "../utils/Shop";
import { ScreenOrientation } from "../utils/types";

interface PriceShowItem {
    icon: JSX.Element;
    name: string;
    href: string;
    onClick?: () => void;
}

interface MultiScreenPriceShowItem {
    icon: JSX.Element;
    name: string;
    panel: {
        id: string;
        title: string;
        content: JSX.Element;
    };
}

const normalContextMenuPopoverId = "normalContextMenuPopover";

export const PriceShowSwitcher = () => {
    const [isPopoverOpen, setPopover] = useState(false);
    const { shopId, isUsingMultiScreen } = useContext(ShopContext);
    const { screenOptions } = useScreensQuery();
    const filteredScreens = screenOptions.filter((s) => s.orientation !== ScreenOrientation.MOBILE);
    const sortedScreens = filteredScreens.sort((a, b) => a.name.localeCompare(b.name));

    const priceShowList: PriceShowItem[] = [
        ...toOptionalArrayEntry(
            {
                icon: <EuiIcon type="eye" size="m" />,
                name: "Standaard show",
                href: `/pricelist/${shopId}`,
            },
            !["470f3f5a-e7b9-43a5-bbfd-2ffce8c161e4", "f155b35a-14a1-4866-867f-93fe21e9769b"].includes(shopId)
        ),
        ...toOptionalArrayEntry(
            {
                icon: <EuiIcon type="eye" size="m" />,
                name: "Standaard show (Portrait)",
                href: `/pricelist/${shopId}?mode=portrait`,
            },
            shopId === "f155b35a-14a1-4866-867f-93fe21e9769b"
        ),
        ...toOptionalArrayEntry(
            {
                icon: <EuiIcon type="eye" size="m" />,
                name: "Standaard show - Light mode (Portrait)",
                href: `/pricelist/${shopId}/light?mode=portrait`,
            },
            shopId === "f155b35a-14a1-4866-867f-93fe21e9769b"
        ),
        ...toOptionalArrayEntry(
            {
                icon: <EuiIcon type="eye" size="m" />,
                name: "Extra show (Portrait)",
                href: `/pricelist/${shopId}/extra?mode=portrait`,
            },
            shopId === "f155b35a-14a1-4866-867f-93fe21e9769b"
        ),
        ...toOptionalArrayEntry(
            {
                icon: <EuiIcon type="eye" size="m" />,
                name: "Extra show - Light mode (Portrait)",
                href: `/pricelist/${shopId}/light/extra?mode=portrait`,
            },
            shopId === "f155b35a-14a1-4866-867f-93fe21e9769b"
        ),
        ...toOptionalArrayEntry(
            {
                icon: <EuiIcon type="eye" size="m" />,
                name: "Horeca show",
                href: `/pricelist/${shopId}/horeca`,
            },
            !["470f3f5a-e7b9-43a5-bbfd-2ffce8c161e4", "f155b35a-14a1-4866-867f-93fe21e9769b"].includes(shopId)
        ),
        //TODO: Enable for all shops when they support the Dynamic Pricelist
        ...toOptionalArrayEntry(
            {
                icon: <EuiIcon type="eye" size="m" />,
                name: "Cannabis show",
                href: `/pricelist/${shopId}/cannabis`,
            },
            ["470f3f5a-e7b9-43a5-bbfd-2ffce8c161e4"].includes(shopId)
        ),
        ...toOptionalArrayEntry(
            {
                icon: <EuiIcon type="eye" size="m" />,
                name: "Portrait cannabis show",
                href: `/pricelist/${shopId}/portrait`,
            },
            [
                "19149768-691c-40d8-a08e-fe900fd23bc0",
                "bbc0cfa7-e230-4338-817a-55190debdac0",
                "ddd70853-5e9d-45ff-82b3-24c9d81d4e32",
                "a08a13e2-a31b-4b6d-b2b4-0491cb3d2227",
            ].includes(shopId)
        ),
    ];

    const onButtonClick = () => {
        setPopover(!isPopoverOpen);
    };

    const closePopover = () => {
        setPopover(false);
    };
    const button = (
        <EuiButtonEmpty iconType="arrowDown" iconSide="right" onClick={onButtonClick}>
            Start a show
        </EuiButtonEmpty>
    );

    const createPanelTree = (Content: React.FC, isPreview: boolean) => {
        const title = isPreview ? "Kies een scherm om te previewen" : "Kies een prijslijst show om te starten:";
        // MULTISCREEN → Only screens + separate Horeca
        if (isUsingMultiScreen) {
            const items = [];

            // Normal screens list
            for (const screen of sortedScreens) {
                const baseHref = `/pricelist/${shopId}/${screen.orientation.toLowerCase()}/${screen.id}`;

                const href = isPreview ? `/preview?url=${encodeURIComponent(baseHref)}` : baseHref;

                items.push({
                    name: isPreview ? `${screen.name} (Preview)` : screen.name,
                    icon: <EuiIcon type={isPreview ? "desktop" : "eye"} size="m" />,
                    href,
                });
            }

            const horecaAllowedShops = [
                "f155b35a-14a1-4866-867f-93fe21e9769b", // Roundabout 69
                "ddd70853-5e9d-45ff-82b3-24c9d81d4e32", // Easy Going
            ];

            if (horecaAllowedShops.includes(shopId)) {
                items.push({
                    name: isPreview ? "Horeca show (Preview)" : "Horeca show",
                    icon: <EuiIcon type={isPreview ? "desktop" : "eye"} size="m" />,
                    href: isPreview
                        ? `/preview?url=${encodeURIComponent(`/pricelist/${shopId}/horeca`)}`
                        : `/pricelist/${shopId}/horeca`,
                });
            }

            return [
                {
                    id: 0,
                    title: title,
                    items,
                },
            ];
        }

        // NON-MULTISCREEN → Keep original behavior
        let items: PriceShowItem[] | MultiScreenPriceShowItem[] = priceShowList.map((item) => ({ ...item }));

        if (isPreview) {
            items.forEach((item: PriceShowItem) => {
                item.name += " (Preview)";
                item.href = `/preview?url=${encodeURIComponent(item.href)}`;
                item.icon = <EuiIcon type="desktop" />;
            });
        }

        return [
            {
                id: 0,
                title: title,
                items,
            },
        ];
    };

    const screenItems =
        sortedScreens?.map((screen) => ({
            name: screen.name,
            icon: <EuiIcon type="eye" size="m" />,
            href: `/pricelist/${shopId}/${screen.orientation.toLowerCase()}/${screen.id}`,
        })) ?? [];

    const previewScreenItems =
        sortedScreens?.map((screen) => ({
            name: screen.name + " (Preview)",
            icon: <EuiIcon type="desktop" size="m" />,
            href: `/preview?url=${encodeURIComponent(
                `/pricelist/${shopId}/${screen.orientation.toLowerCase()}/${screen.id}`
            )}`,
        })) ?? [];

    function GetPanels(isPreview: boolean) {
        console.log("Generating panels, isPreview:", isPreview);
        return [
            {
                id: 0,
                title: "Kies een scherm:",
                items: isPreview ? previewScreenItems : screenItems,
            },
        ];
    }

    interface PanelContentProps {
        isPreview: boolean;
    }

    const PanelContent: React.FC<PanelContentProps> = ({ isPreview }) => {
        console.log("Rendering PanelContent, isPreview:", isPreview);
        return (
            <div>
                <EuiContextMenu initialPanelId={0} panels={GetPanels(isPreview)} />
            </div>
        );
    };

    function GetScreenPanels(isPreview: boolean) {
        return createPanelTree(() => {
            return <PanelContent isPreview={isPreview} />;
        }, isPreview);
    }

    return (
        <EuiPopover
            id={normalContextMenuPopoverId}
            button={button}
            isOpen={isPopoverOpen}
            closePopover={closePopover}
            panelPaddingSize="none"
            anchorPosition="upLeft"
        >
            <EuiContextMenu initialPanelId={0} panels={GetScreenPanels(false)} />
            <EuiSpacer />
            <EuiContextMenu initialPanelId={0} panels={GetScreenPanels(true)} />
        </EuiPopover>
    );
};
